import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddNoteForm from './AddNoteForm';

import type { Note } from '@/apps/Notes/stores/useNotesStore';

import { useNotesStore } from '@/apps/Notes/stores/useNotesStore';
import Modal from '@/common/components/Modal';

interface NoteFormModalProps {
  isOpen: boolean;
  widgetId: string;
  editNote?: Pick<Note, 'id' | 'title' | 'content'> | null;
  initialTitle?: string;
  onClose: () => void;
  onSaved?: (note: Note) => void;
}

const NoteFormModalContent = ({
  widgetId,
  editNote,
  initialTitle,
  onClose,
  onSaved,
}: Omit<NoteFormModalProps, 'isOpen'>) => {
  const addNote = useNotesStore((state) => state.addNote);
  const updateNote = useNotesStore((state) => state.updateNote);

  const [title, setTitle] = useState(editNote?.title ?? initialTitle ?? '');
  const [content, setContent] = useState(editNote?.content ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!title.trim() && !content.trim()) return;

    setIsSaving(true);
    try {
      if (editNote) {
        const success = await updateNote(widgetId, editNote.id, title, content);
        if (success) {
          onClose();
        }
      } else {
        const note = await addNote(widgetId, title, content);
        if (note) {
          onSaved?.(note);
          onClose();
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [title, content, editNote, widgetId, addNote, updateNote, onClose, onSaved]);

  return (
    <AddNoteForm
      content={content}
      isSaving={isSaving}
      title={title}
      onCancel={onClose}
      onContentChange={setContent}
      onSubmit={handleSubmit}
      onTitleChange={setTitle}
    />
  );
};

const NoteFormModal = ({
  isOpen,
  widgetId,
  editNote,
  initialTitle,
  onClose,
  onSaved,
}: NoteFormModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      title={editNote ? t('notes.editNote') : t('notes.addNewNote')}
      onClose={onClose}
    >
      {isOpen && (
        <NoteFormModalContent
          editNote={editNote}
          initialTitle={initialTitle}
          widgetId={widgetId}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Modal>
  );
};

export default NoteFormModal;
